import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDownIcon,
  CheckIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';
import { Clock, Loader2 } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { getTrackIcon } from '@/components/tracks/constants';

// Types matching the full hierarchy
interface StreamingCard {
  title: string;
  description?: string;
  type?: string;
  cardType?: string;
  difficulty?: string;
  priority?: string;
  estimatedMinutes?: number;
}

interface StreamingPattern {
  name: string;
  description?: string;
  difficulty?: string;
  cards?: StreamingCard[];
}

interface StreamingLane {
  name: string;
  description?: string;
  patterns?: StreamingPattern[];
}

interface StreamingMap {
  name: string;
  description?: string;
  lanes?: StreamingLane[];
}

export interface StreamingTrackData {
  trackName?: string;
  trackColor?: string;
  trackIcon?: string;
  trackType?: string;
  maps?: StreamingMap[]; // Full hierarchy
  patterns?: StreamingPattern[]; // Patterns directly (legacy)
  cards?: StreamingCard[]; // Direct cards (for linear tracks)
  isComplete?: boolean;
}

interface StreamingTrackPreviewProps {
  data: StreamingTrackData;
  isStreaming?: boolean;
}

// Google-style checkbox
const Checkbox = ({ checked = false, indeterminate = false }: { checked?: boolean; indeterminate?: boolean }) => (
  <div className={cn(
    'w-[18px] h-[18px] rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-all',
    (checked || indeterminate)
      ? 'bg-[#1a73e8] border-[#1a73e8]'
      : 'border-[#dadce0] dark:border-gray-600 bg-transparent'
  )}>
    {indeterminate && <MinusIcon className="w-3 h-3 text-white" strokeWidth={3} />}
    {checked && !indeterminate && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />}
  </div>
);

// Difficulty Icon (same as AddTrackModal)
const DifficultyIcon = ({ difficulty, size = 12 }: { difficulty?: string; size?: number }) => {
  const colors: Record<string, string> = { easy: '#34a853', medium: '#fbbc04', hard: '#ea4335' };
  const color = difficulty ? colors[difficulty] : '#dadce0';
  const w = size * 0.18, gapSize = size * 0.08;
  const heights = [size * 0.28, size * 0.48, size * 0.72];
  const totalW = w * 3 + gapSize * 2;
  const startX = (size - totalW) / 2;

  if (!difficulty) return null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      {[0, 1, 2].map(i => (
        <rect key={i} x={startX + i * (w + gapSize)} y={size * 0.88 - heights[i]} width={w} height={heights[i]} rx={w / 2} fill={color} />
      ))}
    </svg>
  );
};

// Priority Icon (same as AddTrackModal)
const PriorityIcon = ({ priority, size = 12 }: { priority?: string; size?: number }) => {
  const colors: Record<string, string> = { low: '#34a853', medium: '#fbbc04', high: '#ea4335', urgent: '#ea4335' };
  const color = priority ? colors[priority] : '#dadce0';
  const cx = size / 2, cy = size / 2, oR = size * 0.42, iR = size * 0.24;
  const segs = 12, gap = 0.08;

  const seg = (i: number) => {
    const a = (Math.PI * 2) / segs;
    const st = i * a - Math.PI / 2, en = st + a - gap;
    const o1 = { x: cx + Math.cos(st) * oR, y: cy + Math.sin(st) * oR };
    const o2 = { x: cx + Math.cos(en) * oR, y: cy + Math.sin(en) * oR };
    const i1 = { x: cx + Math.cos(en) * iR, y: cy + Math.sin(en) * iR };
    const i2 = { x: cx + Math.cos(st) * iR, y: cy + Math.sin(st) * iR };
    return `M ${o1.x} ${o1.y} A ${oR} ${oR} 0 0 1 ${o2.x} ${o2.y} L ${i1.x} ${i1.y} A ${iR} ${iR} 0 0 0 ${i2.x} ${i2.y} Z`;
  };

  if (!priority) return null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      {[...Array(segs)].map((_, i) => <path key={i} d={seg(i)} fill={color} />)}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={size * 0.38} fontWeight="700">!</text>
    </svg>
  );
};

// Format time (same as AddTrackModal)
const formatTime = (mins?: number) => {
  if (!mins || mins <= 0) return null;
  if (mins >= 10080) return `${Math.round(mins / 10080)}w`;
  if (mins >= 1440) return `${Math.round(mins / 1440)}d`;
  if (mins >= 60) return `${Math.round(mins / 60)}h`;
  return `${mins}m`;
};

// Badge component (same as AddTrackModal)
const Badge = ({ difficulty, minutes, priority }: { difficulty?: string; minutes?: number; priority?: string }) => {
  const timeStr = formatTime(minutes);
  const hasAny = timeStr || difficulty || priority;
  if (!hasAny) return null;

  return (
    <div className="flex items-center gap-1.5">
      {timeStr && (
        <span className="flex items-center gap-0.5 text-[10px] text-[#1a73e8]">
          <Clock className="w-3 h-3" />
          {timeStr}
        </span>
      )}
      {difficulty && <DifficultyIcon difficulty={difficulty} size={12} />}
      {priority && <PriorityIcon priority={priority} size={12} />}
    </div>
  );
};

// Engaging multi-stage loading while AI builds the track
const BUILD_STEPS = [
  { label: 'Researching topic', icon: '🔍', duration: 8000 },
  { label: 'Planning structure', icon: '🏗️', duration: 12000 },
  { label: 'Generating content', icon: '✨', duration: 20000 },
];

const LOADING_TIPS = [
  'Analyzing the best learning path for you...',
  'Organizing topics by difficulty progression...',
  'Adding practice exercises and resources...',
  'Structuring lessons for optimal retention...',
  'Creating a balanced curriculum...',
  'Fine-tuning difficulty levels...',
  'Building hands-on project ideas...',
];

function BuildingProgress() {
  const [activeStep, setActiveStep] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  // Progress through steps based on time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(prev => prev + 1000);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cumulative = 0;
    for (let i = 0; i < BUILD_STEPS.length; i++) {
      cumulative += BUILD_STEPS[i].duration;
      if (elapsed < cumulative) {
        setActiveStep(i);
        return;
      }
    }
    setActiveStep(BUILD_STEPS.length - 1);
  }, [elapsed]);

  // Rotate tips
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % LOADING_TIPS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="px-4 py-5 space-y-4">
      {/* Steps progress */}
      <div className="space-y-2.5">
        {BUILD_STEPS.map((step, i) => {
          const isActive = i === activeStep;
          const isDone = i < activeStep;

          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.15 }}
              className="flex items-center gap-3"
            >
              {/* Step indicator */}
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0 transition-all duration-500',
                isDone
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : isActive
                    ? 'bg-blue-100 dark:bg-blue-900/30'
                    : 'bg-gray-100 dark:bg-gray-800'
              )}>
                {isDone ? (
                  <CheckIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                ) : isActive ? (
                  <motion.span
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    {step.icon}
                  </motion.span>
                ) : (
                  <span className="opacity-40">{step.icon}</span>
                )}
              </div>

              {/* Step label */}
              <div className="flex-1 min-w-0">
                <span className={cn(
                  'text-xs font-medium transition-colors duration-300',
                  isDone
                    ? 'text-green-600 dark:text-green-400'
                    : isActive
                      ? 'text-[#202124] dark:text-gray-100'
                      : 'text-gray-400 dark:text-gray-500'
                )}>
                  {step.label}
                  {isDone && ' — done'}
                </span>

                {/* Progress bar for active step */}
                {isActive && (
                  <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[#1a73e8] rounded-full"
                      initial={{ width: '0%' }}
                      animate={{ width: '85%' }}
                      transition={{ duration: step.duration / 1000, ease: 'easeOut' }}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Skeleton preview */}
      <div className="space-y-2 pt-1">
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 + i * 0.2 }}
            className="flex items-center gap-2.5 px-2 py-1.5"
          >
            <div className="w-4 h-4 rounded-sm border-2 border-gray-200 dark:border-gray-700 flex-shrink-0" />
            <div
              className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
              style={{ width: `${60 + Math.random() * 30}%`, animationDelay: `${i * 300}ms` }}
            />
          </motion.div>
        ))}
      </div>

      {/* Rotating tip */}
      <AnimatePresence mode="wait">
        <motion.p
          key={tipIndex}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.3 }}
          className="text-[11px] text-center text-[#5f6368] dark:text-gray-400 italic"
        >
          {LOADING_TIPS[tipIndex]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

export function StreamingTrackPreview({ data, isStreaming = true }: StreamingTrackPreviewProps) {
  const [expandedPatterns, setExpandedPatterns] = useState<Set<number>>(new Set([0]));

  // Calculate totals from full hierarchy
  const stats = useMemo(() => {
    let totalCards = 0;
    let totalMinutes = 0;

    // Handle full hierarchy: maps → lanes → patterns → cards
    if (data.maps) {
      data.maps.forEach(map => {
        if (map.lanes) {
          map.lanes.forEach(lane => {
            if (lane.patterns) {
              lane.patterns.forEach(pattern => {
                if (pattern.cards) {
                  totalCards += pattern.cards.length;
                  pattern.cards.forEach(c => {
                    totalMinutes += c.estimatedMinutes || 30;
                  });
                }
              });
            }
          });
        }
      });
    }

    // Handle patterns directly (legacy)
    if (data.patterns) {
      data.patterns.forEach(p => {
        if (p.cards) {
          totalCards += p.cards.length;
          p.cards.forEach(c => {
            totalMinutes += c.estimatedMinutes || 30;
          });
        }
      });
    }

    // Handle direct cards (linear tracks)
    if (data.cards) {
      totalCards += data.cards.length;
      data.cards.forEach(c => {
        totalMinutes += c.estimatedMinutes || 30;
      });
    }

    return { totalCards, totalMinutes };
  }, [data]);

  const togglePattern = (index: number) => {
    setExpandedPatterns(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const hasMapHierarchy = data.maps && data.maps.length > 0;
  const hasPatterns = data.patterns && data.patterns.length > 0;
  const hasCards = data.cards && data.cards.length > 0;
  const isHierarchical = hasMapHierarchy || hasPatterns;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'rounded-lg border overflow-hidden',
        'bg-white dark:bg-gray-900',
        'border-[#dadce0] dark:border-gray-700',
        'shadow-sm'
      )}
    >
      {/* Header - matches AddTrackModal header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e8eaed] dark:border-gray-700 bg-[#f8f9fa] dark:bg-gray-800">
        {/* Track Icon */}
        {(() => {
          const IconComponent = getTrackIcon(data.trackIcon || 'target');
          return (
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: data.trackColor || '#6366f1' }}
            >
              <IconComponent className="w-5 h-5 text-white" />
            </div>
          );
        })()}

        {/* Track Name and Stats */}
        <div className="flex-1 min-w-0">
          {data.trackName ? (
            <h3 className="font-medium text-sm text-[#202124] dark:text-gray-100 truncate">
              {data.trackName}
            </h3>
          ) : (
            <div className="h-4 w-32 bg-[#dadce0] dark:bg-gray-700 rounded animate-pulse" />
          )}
          <p className="text-[11px] text-[#5f6368] dark:text-gray-400">
            {stats.totalCards} items
          </p>
        </div>

        {/* Streaming indicator - only show if no content yet */}
        {isStreaming && stats.totalCards === 0 && (
          <Loader2 className="w-4 h-4 text-[#1a73e8] animate-spin" />
        )}
      </div>

      {/* Content */}
      <div className="max-h-[300px] overflow-y-auto">
        {/* Full Hierarchy View (maps → lanes → patterns → cards) */}
        {hasMapHierarchy && (
          <div className="p-2 space-y-2">
            {data.maps!.map((map, mapIdx) => (
              <motion.div
                key={mapIdx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[#dadce0] dark:border-gray-600 rounded-lg overflow-hidden"
              >
                {/* Map Header */}
                <div className="px-3 py-2 bg-[#f8f9fa] dark:bg-gray-800 border-b border-[#e8eaed] dark:border-gray-700">
                  <span className="text-sm font-semibold text-[#202124] dark:text-gray-100">
                    {map.name || 'Loading...'}
                  </span>
                  <span className="ml-2 text-[11px] text-[#5f6368] dark:text-gray-400">
                    {map.lanes?.length || 0} lanes
                  </span>
                </div>

                {/* Lanes */}
                {map.lanes && map.lanes.length > 0 && (
                  <div className="p-2 space-y-2">
                    {map.lanes.map((lane, laneIdx) => (
                      <div key={laneIdx} className="border border-[#e8eaed] dark:border-gray-700 rounded-md overflow-hidden">
                        {/* Lane Header */}
                        <div className="px-2.5 py-1.5 bg-white dark:bg-gray-800 border-b border-[#e8eaed] dark:border-gray-700">
                          <span className="text-[13px] font-medium text-[#202124] dark:text-gray-100">
                            {lane.name}
                          </span>
                          <span className="ml-2 text-[10px] text-[#5f6368] dark:text-gray-400">
                            {lane.patterns?.length || 0} patterns
                          </span>
                        </div>

                        {/* Patterns */}
                        {lane.patterns && lane.patterns.length > 0 && (
                          <div className="p-1.5 space-y-1">
                            {lane.patterns.map((pattern, patternIdx) => (
                              <div key={patternIdx} className="border border-[#e8eaed] dark:border-gray-700 rounded">
                                {/* Pattern Header */}
                                <div className="flex items-center gap-2 px-2 py-1.5 bg-[#f8f9fa] dark:bg-gray-800/50">
                                  <span className="flex-1 text-[12px] font-medium text-[#3c4043] dark:text-gray-200 truncate">
                                    {pattern.name}
                                  </span>
                                  <span className="text-[10px] text-[#5f6368] dark:text-gray-400">
                                    {pattern.cards?.length || 0} cards
                                  </span>
                                </div>

                                {/* Cards */}
                                {pattern.cards && pattern.cards.length > 0 && (
                                  <div className="px-2 py-1 space-y-0.5">
                                    {pattern.cards.slice(0, 5).map((card, cardIdx) => (
                                      <div key={cardIdx} className="flex items-center gap-1.5 py-0.5">
                                        <Checkbox checked={false} />
                                        <span className="flex-1 text-[11px] text-[#5f6368] dark:text-gray-400 truncate">
                                          {card.title}
                                        </span>
                                        {card.estimatedMinutes && (
                                          <span className="text-[9px] text-[#1a73e8]">
                                            {formatTime(card.estimatedMinutes)}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    {pattern.cards.length > 5 && (
                                      <div className="text-[10px] text-[#5f6368] dark:text-gray-400 pl-5">
                                        +{pattern.cards.length - 5} more
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* Legacy Hierarchical View (patterns directly) */}
        {hasPatterns && !hasMapHierarchy && (
          <div className="p-2">
            <AnimatePresence mode="popLayout">
              {data.patterns!.map((pattern, patternIdx) => {
                const isExpanded = expandedPatterns.has(patternIdx);
                const patternHasCards = pattern.cards && pattern.cards.length > 0;

                return (
                  <motion.div
                    key={patternIdx}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border border-[#e8eaed] dark:border-gray-700 rounded-lg mb-2 overflow-hidden"
                  >
                    {/* Pattern Header */}
                    <div className="flex items-center gap-2 py-2 pl-3 pr-3 hover:bg-[#f1f3f4] dark:hover:bg-gray-800 transition-colors">
                      <Checkbox checked={false} />

                      {patternHasCards ? (
                        <button
                          onClick={() => togglePattern(patternIdx)}
                          className="p-0.5 hover:bg-[#e8eaed] dark:hover:bg-gray-700 rounded"
                        >
                          <ChevronDownIcon
                            className={cn(
                              'w-4 h-4 text-[#5f6368] transition-transform',
                              !isExpanded && '-rotate-90'
                            )}
                          />
                        </button>
                      ) : (
                        <div className="w-5" />
                      )}

                      <span className="flex-1 text-sm text-[#202124] dark:text-gray-100 truncate font-medium">
                        {pattern.name || 'Loading...'}
                      </span>

                      <div className="flex items-center gap-2 shrink-0">
                        {patternHasCards && (
                          <span className="text-[11px] text-[#5f6368] dark:text-gray-400">
                            {pattern.cards!.length}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Pattern Cards */}
                    <AnimatePresence>
                      {isExpanded && patternHasCards && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-[#e8eaed] dark:border-gray-700"
                        >
                          <div className="ml-10 py-1 space-y-0.5">
                            {pattern.cards!.map((card, cardIdx) => (
                              <motion.div
                                key={cardIdx}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: cardIdx * 0.03 }}
                                className="flex items-center gap-2 py-1.5 pl-2 pr-3 rounded hover:bg-[#f1f3f4] dark:hover:bg-gray-800 transition-colors"
                              >
                                <Checkbox checked={false} />
                                <span className="flex-1 text-[13px] text-[#3c4043] dark:text-gray-300 truncate">
                                  {card.title}
                                </span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <Badge
                                    difficulty={card.difficulty}
                                    minutes={card.estimatedMinutes}
                                    priority={card.priority}
                                  />
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Linear View (direct cards) */}
        {!isHierarchical && hasCards && (
          <div className="p-2 space-y-0.5">
            <AnimatePresence mode="popLayout">
              {data.cards!.map((card, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="flex items-center gap-2 py-2 px-3 rounded hover:bg-[#f1f3f4] dark:hover:bg-gray-800 transition-colors"
                >
                  <Checkbox checked={false} />
                  <span className="flex-1 text-[13px] text-[#202124] dark:text-gray-200 truncate">
                    {card.title}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge
                      difficulty={card.difficulty}
                      minutes={card.estimatedMinutes}
                      priority={card.priority}
                    />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Empty State / Building Loading */}
        {!hasMapHierarchy && !hasPatterns && !hasCards && (
          <BuildingProgress />
        )}

        {/* Streaming indicator at bottom */}
        {isStreaming && (hasMapHierarchy || hasPatterns || hasCards) && (
          <div className="px-3 py-2 border-t border-[#e8eaed] dark:border-gray-700 flex items-center gap-2 text-[11px] text-[#5f6368] dark:text-gray-400">
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-[#1a73e8]"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </div>
            <span>Generating content...</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default StreamingTrackPreview;
